import { useState, useEffect, useCallback } from 'react';
import { isLoggedIn, isGuest, setGuest, getStoredUser, getGameState, logout } from './services/api';
import { sdkSession } from './auth/catalyst';
import { fetchMe } from './auth/api';
import { hanumanJi } from './assets/images';
import { useLang, TOTAL_PEETHS, TOTAL_JYOTIRLINGAS } from './i18n';
import LangToggle from './components/LangToggle';
import Toast from './components/Toast';
import AuthScreen from './screens/AuthScreen';
import GameScreen from './screens/GameScreen';
import LeaderboardScreen from './screens/LeaderboardScreen';
import ProfileScreen from './screens/ProfileScreen';
import AdminScreen from './screens/AdminScreen';
import PeethsScreen from './screens/PeethsScreen';
import JyotirlingsScreen from './screens/JyotirlingsScreen';
import MapScreen from './screens/MapScreen';
import AnimatedCounter from './components/AnimatedCounter';
import HanumanHero from './components/HanumanHero';
import { OmIcon } from './components/SacredIcons';

// Build-time auth mode: 'legacy' (custom OTP/JWT, default) | 'catalyst' (Embedded Auth).
const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'legacy';

// ── Card icon chip ────────────────────────────────────────────────────────────
function CardIcon({ badge, img, emoji }) {
  const base = {
    width: 52, height: 52,
    borderRadius: 12,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(240, 215, 175, 0.32)',
    border: '1px solid rgba(210, 175, 120, 0.22)',
    overflow: 'hidden',
    flexShrink: 0,
  };

  if (img) {
    return (
      <div className="menu-card-icon-chip" style={{ ...base, background: 'rgba(245, 225, 195, 0.45)', border: '1px solid rgba(210,180,130,0.20)' }}>
        <img src={img} alt="" style={{ width: 36, height: 36, objectFit: 'contain' }} loading="lazy" />
      </div>
    );
  }

  if (emoji) {
    return (
      <div className="menu-card-icon-chip" style={base}>
        <span style={{ fontSize: 24, lineHeight: 1 }}>
          {emoji}
        </span>
      </div>
    );
  }

  // Text badge (MAP, #1, ID, ADM)
  const long = badge && badge.length > 2;
  return (
    <div className="menu-card-icon-chip" style={base}>
      <span style={{
        fontFamily: "'Cinzel Decorative', serif",
        fontSize: long ? 10 : 14,
        fontWeight: 700,
        color: 'var(--saffron-dark)',
        letterSpacing: long ? 0.4 : 0.8,
        textAlign: 'center',
        lineHeight: 1,
      }}>
        {badge}
      </span>
    </div>
  );
}

// ── Menu Screen ───────────────────────────────────────────────────────────────
function MenuScreen({
  user, isGuestMode,
  onPlay, onPeeths, onJyotirlingas, onMap, onLeaderboard, onProfile, onAdmin, onLogout,
}) {
  const { t }    = useLang();
  const totalRam = user?.total_ram_count || 0;
  const peeths   = user?.shakti_peeths_visited || 0;

  const menuItems = [
    { icon: <CardIcon img={hanumanJi} />,  label: t('menu.play'),                                       action: onPlay },
    { icon: <CardIcon emoji="🛕" />,        label: t('menu.peeths', { total: TOTAL_PEETHS }),            action: onPeeths },
    { icon: <CardIcon badge="MAP" />,       label: t('menu.map'),                                        action: onMap },
    { icon: <CardIcon badge="#1" />,        label: t('menu.leaderboard'),                                action: onLeaderboard },
    { icon: <CardIcon badge="ID" />,        label: t('menu.profile'),                                    action: onProfile },
    { icon: <CardIcon emoji="🔱" />,        label: t('menu.jyotirlingas', { total: TOTAL_JYOTIRLINGAS }), action: onJyotirlingas },
  ];

  if (user?.role === 'admin') {
    menuItems.push({ icon: <CardIcon badge="ADM" />, label: t('menu.admin'), action: onAdmin });
  }

  return (
    <div className="screen menu-screen">
      <div className="menu-container">

        {/* ── User bar ── */}
        <div className="user-bar">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
            <div style={{
              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, var(--saffron), var(--saffron-dark))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'var(--font-display)', fontSize: 15,
              color: '#fff', fontWeight: 700,
              boxShadow: '0 2px 8px rgba(230,81,0,0.22)',
            }}>
              {user ? user.name.charAt(0).toUpperCase() : 'अ'}
            </div>
            <span className="user-name" style={{
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              fontFamily: 'var(--font-body)',
              fontSize: 14,
              fontWeight: 700,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {user ? user.name : t('common.guest')}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <LangToggle variant="light" />
            {(!isGuestMode && user) || isGuestMode ? (
              <button
                onClick={onLogout}
                title={isGuestMode ? t('menu.login') : t('menu.logout')}
                aria-label={isGuestMode ? t('menu.login') : t('menu.logout')}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: 44, height: 44,
                  background: 'rgba(255,107,0,0.09)',
                  border: '1px solid rgba(255,107,0,0.22)',
                  borderRadius: '50%',
                  color: 'var(--saffron-dark)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  touchAction: 'manipulation',
                  WebkitTapHighlightColor: 'transparent',
                  transition: 'var(--transition)',
                }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(255,107,0,0.18)'}
                onMouseOut={e => e.currentTarget.style.background = 'rgba(255,107,0,0.09)'}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </button>
            ) : null}
          </div>
        </div>

        {/* ── Hanuman ── */}
        <HanumanHero size="small" />

        {/* ── RAM count ── */}
        <div className="ram-count-display" style={{ width: '100%' }}>
          <div className="ram-count-number">
            <AnimatedCounter value={totalRam} duration={1000} />
          </div>
          <div className="ram-count-label">{t('menu.ramLabel')}</div>
          {peeths > 0 && (
            <div style={{
              fontFamily: 'var(--font-hindi)', fontSize: 13,
              color: 'var(--gold)', marginTop: 10, opacity: 0.9,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            }}>
              <OmIcon size={16} color="var(--gold)" />
              <span>{t('menu.peethsDone', { n: peeths, total: TOTAL_PEETHS })}</span>
            </div>
          )}
        </div>

        {/* ── Menu grid ── */}
        <div className="menu-grid" style={{ width: '100%' }}>
          {menuItems.map((item, i) => (
            <button
              key={i}
              className="menu-card"
              onClick={() => { if (navigator.vibrate) navigator.vibrate(10); item.action(); }}
            >
              {/* Icon chip */}
              {item.icon}

              {/* Label */}
              <div className="menu-card-label" style={{ marginTop: 4 }}>
                {item.label}
              </div>
            </button>
          ))}
        </div>

      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
export default function App() {
  const { t }                     = useLang();
  const [screen, setScreen]       = useState('loading');
  const [user, setUser]           = useState(null);
  const [guestMode, setGuestMode] = useState(false);

  const init = useCallback(async () => {
    if (AUTH_MODE === 'catalyst') {
      // Guest takes precedence if explicitly chosen (no account needed).
      if (isGuest()) { setGuestMode(true); setScreen('menu'); return; }
      const session = await sdkSession();
      // SDK loaded and DEFINITELY no session -> safe to show the login widget.
      if (session.available && !session.authenticated) { setScreen('auth'); return; }
      // Session exists (or SDK absent in plain CRA dev): backend resolves identity.
      try {
        const data = await fetchMe();
        setUser(data.user || data);
        setScreen('menu');
      } catch (err) {
        // Signed in but backend rejected -> stable error screen (never re-show
        // login over a live session — that causes the signin-redirect loop).
        setScreen(session.authenticated ? 'sessionError' : 'auth');
      }
      return;
    }

    // ── Legacy path (unchanged) ──
    if (isLoggedIn()) {
      const stored = getStoredUser();
      setUser(stored);
      getGameState()
        .then(data => { if (data.user) setUser(data.user); })
        .catch(() => {});
      setScreen('menu');
    } else if (isGuest()) {
      setGuestMode(true);
      setScreen('menu');
    } else {
      setScreen('auth');
    }
  }, []);

  useEffect(() => { init(); }, [init]);

  function handleLogin(userData) {
    setUser(userData);
    setGuestMode(false);
    setGuest(false);
    setScreen('menu');
  }

  function handleGuest() {
    setGuest(true);
    setGuestMode(true);
    setUser(null);
    setScreen('menu');
  }

  async function handleLogout() {
    // Catalyst-authenticated (non-guest): hand off to Catalyst's signOut, which
    // clears the session cookie and redirects back to the app.
    if (AUTH_MODE === 'catalyst' && !guestMode) {
      setGuest(false);
      const sdk = window.catalyst;
      if (sdk?.auth?.signOut) {
        sdk.auth.signOut(`${window.location.origin}${window.location.pathname}`);
        return; // Catalyst redirects/reloads from here
      }
      setUser(null);
      setScreen('auth');
      return;
    }
    // Legacy, and guest in either mode: clear locally.
    await logout();
    setUser(null);
    setGuestMode(false);
    setScreen('auth');
  }

  function handleUpdateUser(patch) {
    setUser(prev => prev ? { ...prev, ...patch } : patch);
  }

  if (screen === 'loading') {
    return (
      <div className="loading-overlay">
        <HanumanHero size="large" />
        <div className="loading-text">{t('common.loading')}</div>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <>
      <div className="bg-pattern" aria-hidden />
      <Toast />

      {screen === 'auth' && (
        <AuthScreen onLogin={handleLogin} onGuest={handleGuest} />
      )}

      {screen === 'sessionError' && (
        <div className="screen" style={{
          minHeight: '100dvh', display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          padding: '24px 20px', gap: 16,
          background: 'radial-gradient(ellipse at 50% 0%, #3D0015 0%, #1a0000 55%, #0D0000 100%)',
        }}>
          <HanumanHero size="small" />
          <div style={{ fontFamily: 'var(--font-hindi)', fontSize: 20, color: 'var(--gold)' }}>
            सत्र सत्यापित नहीं हुआ
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 15, color: 'rgba(255,248,231,0.7)', maxWidth: 360, lineHeight: 1.6 }}>
            आप साइन इन हैं, पर सत्र की पुष्टि नहीं हो पाई।<br />
            <span style={{ opacity: 0.7 }}>Signed in, but your session couldn’t be verified.</span>
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
            <button
              onClick={() => { setScreen('loading'); init(); }}
              style={{
                minHeight: 52, padding: '0 28px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg, #FF6D00, #E65100)', color: '#fff',
                fontFamily: 'var(--font-body)', fontSize: 17, fontWeight: 700,
                boxShadow: '0 6px 20px rgba(230,81,0,0.5)', touchAction: 'manipulation',
              }}
            >पुनः प्रयास | Retry</button>
            <button
              onClick={handleLogout}
              style={{
                minHeight: 52, padding: '0 24px', borderRadius: 12, cursor: 'pointer',
                background: 'rgba(255,107,0,0.09)', border: '1px solid rgba(255,107,0,0.3)',
                color: 'var(--saffron-dark, #E65100)', fontFamily: 'var(--font-body)', fontSize: 16, fontWeight: 600,
                touchAction: 'manipulation',
              }}
            >साइन आउट | Sign out</button>
          </div>
        </div>
      )}

      {screen === 'menu' && (
        <MenuScreen
          user={user} isGuestMode={guestMode}
          onPlay={() => setScreen('game')}
          onPeeths={() => setScreen('peeths')}
          onJyotirlingas={() => setScreen('jyotirlingas')}
          onMap={() => setScreen('map')}
          onLeaderboard={() => setScreen('leaderboard')}
          onProfile={() => setScreen('profile')}
          onAdmin={() => setScreen('admin')}
          onLogout={handleLogout}
        />
      )}

      {screen === 'game' && (
        <GameScreen user={user} onBack={() => setScreen('menu')} onUpdateUser={handleUpdateUser} />
      )}
      {screen === 'peeths' && (
        <PeethsScreen user={user} isGuest={guestMode} onBack={() => setScreen('menu')} />
      )}
      {screen === 'jyotirlingas' && (
        <JyotirlingsScreen user={user} isGuest={guestMode} onBack={() => setScreen('menu')} />
      )}
      {screen === 'leaderboard' && (
        <LeaderboardScreen user={user} isGuest={guestMode} onBack={() => setScreen('menu')} />
      )}
      {screen === 'profile' && (
        <ProfileScreen user={user} isGuest={guestMode} onBack={() => setScreen('menu')} />
      )}
      {screen === 'admin' && (
        <AdminScreen onBack={() => setScreen('menu')} />
      )}
      {screen === 'map' && (
        <MapScreen user={user} onBack={() => setScreen('menu')} />
      )}
    </>
  );
}
